// ============================================================
// First 90 Days
//
// Tori's queue for new clients: what's due, on whom, and what to do.
// Lauren's view of the same data leans on deliverables vs timeline.
// ============================================================
import { useState, useEffect, useMemo, useCallback } from 'react'
import {
  Check, Clock, AlertTriangle, Copy, ChevronRight, ChevronLeft, Target, RefreshCw, SkipForward,
  GraduationCap, MessageCircle, TrendingUp, Plus, Handshake, Send, PartyPopper, X,
} from 'lucide-react'
import { supabase } from './supabase'
import ClientNotesPanel from './ClientNotesPanel'
import { useToast } from './ToastContext'
import { DONE_STATUSES } from './constants'
import {
  TOUCHPOINTS, ESTABLISHING_TOUCHPOINTS, getTouchpoint, HEALTH, dayNumber, touchpointState,
  STATE_STYLE, fillTemplate, PHASES,
  GOAL_STATUS, COMMITMENT_STATUS, COMMITMENT_SOURCE, commitmentStats, goalProgress,
  GOAL_TYPES, NOTE_SOURCES, daysUntil,
  FLAGS, FLAG_GROUPS, getFlag, flagSummary, REVIEW_PROMPTS, REVIEW_CADENCE_DAYS,
} from './first90Config'

const fmtDate = d => d ? new Date(String(d).slice(0, 10) + 'T12:00:00')
  .toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'

// ── Pill ────────────────────────────────────────────────────────────────────
function Pill({ state, children }) {
  const s = STATE_STYLE[state] || STATE_STYLE.none
  return (
    <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 50,
      background: s.bg === 'transparent' ? 'var(--cream-dark)' : s.bg, color: s.color }}>
      {children || s.label}
    </span>
  )
}

// ── Kickoff captures ────────────────────────────────────────────────────────
// The context gathered at kickoff. Goals themselves live in the Goals panel —
// this is everything else that turned out to matter when clients left.
function KickoffNotes({ row, onSave }) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState({})
  useEffect(() => { setDraft(row || {}) }, [row])
  const f = (k, v) => setDraft(d => ({ ...d, [k]: v }))

  const FIELDS = [
    { k: 'other_influencers', label: 'Who else has a say in continuing this?',
      hint: 'Partner, practice manager, accountant.' },
    { k: 'promised_off_contract', label: 'Promised in the sales call but not in the contract',
      hint: 'The single most common source of "you said you would".' },
    { k: 'too_slow_looks_like', label: 'What does "too slow" look like to them?',
      hint: 'The point at which they would start to worry.' },
    { k: 'tried_before', label: 'What did they try before, and why did it stop?' },
    { k: 'monthly_ceiling', label: 'Realistic monthly ceiling',
      hint: 'So a stretch month is not mistaken for a comfortable one.' },
  ]

  const filled = FIELDS.filter(x => row?.[x.k])

  if (!editing) {
    return (
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-header">
          <span className="card-title">Kickoff notes</span>
          <button className="btn btn-ghost btn-sm" onClick={() => setEditing(true)}>
            {filled.length ? 'Edit' : 'Add'}
          </button>
        </div>
        <div className="card-body">
          {filled.length === 0 ? (
            <div style={{ fontSize: 12.5, color: 'var(--text-muted)', fontStyle: 'italic' }}>
              Nothing captured yet. Worth five minutes after the kickoff.
            </div>
          ) : filled.map(x => (
            <div key={x.k} style={{ marginBottom: 9 }}>
              <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4,
                color: 'var(--text-muted)' }}>{x.label}</div>
              <div style={{ fontSize: 13,
                color: x.k === 'promised_off_contract' ? '#8a3a3a' : 'var(--text-primary)' }}>
                {row[x.k]}
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="card" style={{ marginBottom: 14 }}>
      <div className="card-header"><span className="card-title">Kickoff notes</span></div>
      <div className="card-body">
        {FIELDS.map(x => (
          <div className="form-group" key={x.k}>
            <label className="form-label">{x.label}</label>
            {x.hint && (
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 3 }}>{x.hint}</div>
            )}
            <input className="form-control" value={draft[x.k] || ''}
              onChange={e => f(x.k, e.target.value)} />
          </div>
        ))}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost btn-sm" onClick={() => setEditing(false)}>Cancel</button>
          <button className="btn btn-gold btn-sm"
            onClick={async () => { const ok = await onSave(draft); if (ok !== false) setEditing(false) }}>
            Save
          </button>
        </div>
      </div>
    </div>
  )
}

// ── One touchpoint ──────────────────────────────────────────────────────────
function TouchpointCard({ tp, def, client, goal, onComplete, onSkip, expanded, onToggle }) {
  const toast = useToast()
  const [note, setNote] = useState('')
  const [health, setHealth] = useState('')
  const state = touchpointState(tp)
  const s = STATE_STYLE[state]

  const copyTemplate = () => {
    const first = (client.primary_contact_name || '').trim().split(/\s+/)[0] || 'there'
    const text = fillTemplate(def.template, {
      first, client: client.name,
      goals_recap: '[the goals we agreed on at kickoff]',
      timeline_recap: '[the dates/milestones we agreed on]',
      kickoff_changes: '[anything that changed on the kickoff call — or delete this sentence]',
      in_progress: '[what the team is working on]',
      first_visible: '[first visible deliverable]',
      first_visible_when: '[when]',
      sender: '[your name]',
    })
    navigator.clipboard.writeText(text)
      .then(() => toast('Draft copied — fill the bracketed parts', 'success'))
      .catch(() => toast('Could not copy', 'error'))
  }

  return (
    <div style={{ border: `1px solid ${state === 'overdue' ? '#f0c4c4' : 'var(--border-light)'}`,
      borderRadius: 10, marginBottom: 8, background: state === 'done' ? 'var(--cream)' : '#fff' }}>
      <div onClick={onToggle} style={{ padding: '11px 14px', cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
          background: state === 'done' ? 'var(--green)' : s.bg === 'transparent' ? 'var(--cream-dark)' : s.bg,
          display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {state === 'done' ? <Check size={13} color="#fff" />
            : state === 'overdue' ? <AlertTriangle size={12} color="var(--red)" />
            : <Clock size={12} color={s.color} />}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 500,
            textDecoration: state === 'skipped' ? 'line-through' : 'none' }}>
            {def.label}
            {def.internal && <span style={{ fontSize: 10.5, color: 'var(--text-muted)', marginLeft: 6 }}>internal</span>}
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
            {def.owner} · due {fmtDate(tp?.due_date)}
            {tp?.completed_at && ` · done ${fmtDate(tp.completed_at)}`}
          </div>
        </div>
        <Pill state={state} />
        <ChevronRight size={14} style={{ color: 'var(--text-muted)', flexShrink: 0,
          transform: expanded ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }} />
      </div>

      {expanded && (
        <div style={{ padding: '0 14px 14px', borderTop: '1px solid var(--border-light)' }}>
          <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', margin: '10px 0' }}>{def.purpose}</div>

          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: 0.4, color: 'var(--text-muted)', marginBottom: 5 }}>Checklist</div>
          <ul style={{ fontSize: 12.5, lineHeight: 1.7, paddingLeft: 18, margin: '0 0 10px' }}>
            {def.checklist.map((c, i) => <li key={i}>{c}</li>)}
          </ul>

          {def.escalate && (
            <>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                letterSpacing: 0.4, color: 'var(--red)', marginBottom: 5 }}>Escalate if</div>
              <ul style={{ fontSize: 12.5, lineHeight: 1.7, paddingLeft: 18, margin: '0 0 10px', color: '#8a3a3a' }}>
                {def.escalate.map((c, i) => <li key={i}>{c}</li>)}
              </ul>
            </>
          )}

          {def.template && (
            <button className="btn btn-ghost btn-sm" onClick={copyTemplate} style={{ marginBottom: 10 }}>
              <Copy size={12} /> Copy draft note
            </button>
          )}

          {def.setsHealth && !tp?.completed_at && (
            <div className="form-group">
              <label className="form-label">Client health at 90 days</label>
              <select className="form-control" value={health} onChange={e => setHealth(e.target.value)}>
                <option value="">— Select —</option>
                {Object.entries(HEALTH).map(([k, v]) =>
                  <option key={k} value={k}>{v.label} — {v.note}</option>)}
              </select>
            </div>
          )}

          {!tp?.completed_at && (
            <>
              <textarea className="form-control" rows={2} value={note}
                onChange={e => setNote(e.target.value)}
                placeholder="What came out of it? Their words are more useful than a summary."
                style={{ fontSize: 12.5, resize: 'vertical' }} />
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
                <button className="btn btn-ghost btn-sm" onClick={() => onSkip(tp)}>
                  <SkipForward size={12} /> Not needed
                </button>
                <button className="btn btn-gold btn-sm"
                  disabled={def.setsHealth && !health}
                  title={def.setsHealth && !health ? 'Set the health status first' : undefined}
                  onClick={() => onComplete(tp, note, health)}>
                  <Check size={12} /> Mark done
                </button>
              </div>
            </>
          )}

          {tp?.notes && (
            <div style={{ marginTop: 10, padding: '9px 11px', background: 'var(--cream)',
              borderRadius: 8, fontSize: 12.5, whiteSpace: 'pre-wrap' }}>{tp.notes}</div>
          )}
        </div>
      )}
    </div>
  )
}

// Touchpoints as a compact strip. They matter, but they're a schedule — the
// goals are the thing being watched, so these sit on top and stay small.
function TouchpointStrip({ tps, defs, onOpen, activeKind }) {
  if (!tps.length) return null
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
      {defs.map(def => {
        const tp = tps.find(t => t.kind === def.kind)
        if (!tp) return null
        const state = touchpointState(tp)
        const st = STATE_STYLE[state]
        const open = activeKind === def.kind
        return (
          <button key={def.kind} onClick={() => onOpen(open ? null : def.kind)}
            title={`${def.label} — ${def.owner} · due ${fmtDate(tp.due_date)}`}
            style={{ flex: 1, minWidth: 118, textAlign: 'left', cursor: 'pointer',
              padding: '8px 11px', borderRadius: 9,
              border: `1.5px solid ${open ? 'var(--navy)' : state === 'overdue' ? 'var(--red)' : 'var(--border-light)'}`,
              background: state === 'done' ? 'var(--lightgreen-bg)'
                : state === 'overdue' ? 'var(--red-bg)'
                : state === 'due' ? '#fff8e1' : '#fff' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 15, height: 15, borderRadius: 50, flexShrink: 0, fontSize: 9,
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700,
                background: state === 'done' ? 'var(--green)' : st.color, color: '#fff' }}>
                {state === 'done' ? '✓' : def.day}
              </span>
              <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--text-primary)' }}>
                {def.short === 'Send' ? 'Welcome' : def.label.split('—')[0].trim()}
              </span>
            </div>
            <div style={{ fontSize: 10.5, color: st.color, marginTop: 2 }}>
              {state === 'done' ? 'Done' : state === 'overdue' ? 'Overdue' : fmtDate(tp.due_date)}
            </div>
          </button>
        )
      })}
    </div>
  )
}

// ── Delivery progress — Lauren's half ───────────────────────────────────────
function DeliveryPanel({ client, tasks, onOpenTasks }) {
  const ct = tasks.filter(t => t.client_id === client.id && !t._virtual)
  const done = ct.filter(t => DONE_STATUSES.includes(t.status))
  const overdue = ct.filter(t => t.date_due && new Date(t.date_due) < new Date()
    && !DONE_STATUSES.includes(t.status))
  const blocked = ct.filter(t => t.status === 'blocked')
  const clientVisible = done.filter(t => t.client_visible)

  return (
    <div className="card" style={{ marginTop: 14 }}>
      <div className="card-header">
        <span className="card-title">Delivery</span>
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{ct.length} tasks</span>
      </div>
      <div className="card-body">
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
          {[
            { label: 'All tasks', v: ct.length, c: 'var(--navy)',
              filters: { filterClient: client.id } },
            { label: 'Completed', v: done.length, c: 'var(--green)',
              filters: { filterClient: client.id, filterStatuses: ['approved', 'live'] } },
            { label: 'Overdue', v: overdue.length, c: overdue.length ? 'var(--red)' : 'var(--text-muted)',
              filters: { filterClient: client.id, dueFilter: 'overdue', hideCompleted: true } },
            { label: 'Blocked', v: blocked.length, c: blocked.length ? 'var(--red)' : 'var(--text-muted)',
              filters: { filterClient: client.id, filterStatuses: ['blocked'] } },
          ].map(s => {
            const clickable = onOpenTasks && s.v > 0
            return (
              <button key={s.label} disabled={!clickable}
                onClick={() => clickable && onOpenTasks(s.filters)}
                title={clickable ? `See these ${s.v} in All Tasks` : undefined}
                style={{ flex: 1, minWidth: 110, padding: '10px 12px', textAlign: 'left',
                  borderRadius: 9, border: '1px solid var(--border-light)', background: '#fff',
                  cursor: clickable ? 'pointer' : 'default',
                  transition: 'transform .08s, box-shadow .08s' }}
                onMouseEnter={e => { if (clickable) {
                  e.currentTarget.style.transform = 'translateY(-1px)'
                  e.currentTarget.style.boxShadow = 'var(--shadow-md)' } }}
                onMouseLeave={e => {
                  e.currentTarget.style.transform = 'none'
                  e.currentTarget.style.boxShadow = 'none' }}>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, color: s.c }}>{s.v}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{s.label}</div>
                {clickable && (
                  <div style={{ fontSize: 10, color: s.c, fontWeight: 600, marginTop: 2 }}>
                    View →
                  </div>
                )}
              </button>
            )
          })}
        </div>

        {/* The question that matters at day 30: is any of this visible to them? */}
        {done.length > 0 && clientVisible.length === 0 && (
          <div style={{ fontSize: 12.5, padding: '9px 11px', borderRadius: 8,
            background: '#fff8e1', color: '#8a6000' }}>
            {done.length} tasks completed but none marked client-visible. From their side this
            looks like nothing has happened — worth checking before the day-30 call.
          </div>
        )}
        {overdue.length > 0 && (
          <div style={{ fontSize: 12.5, marginTop: 8 }}>
            <strong style={{ color: 'var(--red)' }}>Overdue:</strong>{' '}
            {overdue.slice(0, 4).map(t => t.title).join(' · ')}
            {overdue.length > 4 && ` +${overdue.length - 4} more`}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Goals ───────────────────────────────────────────────────────────────────
// Several per client. Two kinds: a number to move, or a thing to achieve.
// Each carries its own notes thread so feedback lands on the goal it concerns.
function GoalCard({ goal, checkins, onUpdate, onCheckin }) {
  const [tab, setTab] = useState(null)      // 'number' | 'note' | null
  const [val, setVal] = useState('')
  const [note, setNote] = useState('')
  const [source, setSource] = useState('ad')

  const mine = checkins.filter(c => c.goal_id === goal.id)
    .sort((a, b) => b.checked_on.localeCompare(a.checked_on))
  const numbers = mine.filter(c => c.value !== null && c.value !== undefined)
  const notes = mine.filter(c => c.note)
  const latest = numbers[0]
  const pct = goalProgress(goal, latest?.value)
  const st = GOAL_STATUS[goal.status]
  const isMilestone = goal.goal_type === 'milestone'
  const left = daysUntil(goal.target_date)
  const overdue = left !== null && left < 0 && goal.status === 'active'

  return (
    <div
      onMouseEnter={e => { e.currentTarget.style.boxShadow = 'var(--shadow-md)'
        e.currentTarget.style.borderColor = 'var(--navy-mid)' }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'
        e.currentTarget.style.borderColor = overdue ? '#f0c4c4' : 'var(--border-light)' }}
      style={{ border: `1px solid ${overdue ? '#f0c4c4' : 'var(--border-light)'}`,
        borderRadius: 10, marginBottom: 10, overflow: 'hidden',
        transition: 'box-shadow .13s, border-color .13s' }}>

      {/* Header */}
      <div style={{ padding: '11px 13px', background: goal.status === 'active' ? '#fff' : 'var(--cream)' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--navy)' }}>{goal.title}</span>
          {goal.is_primary && (
            <span style={{ fontSize: 9.5, fontWeight: 700, color: 'var(--gold)',
              border: '1px solid var(--gold)', borderRadius: 4, padding: '0 4px' }}>PRIMARY</span>
          )}
          <span style={{ fontSize: 11, fontWeight: 600, color: st.color, marginLeft: 'auto' }}>
            {st.label}
          </span>
        </div>

        {/* Everything written about the goal, not just the numbers. This is
            what an AD needs to read in a check-in without hunting. */}
        {(goal.current_state || goal.target_text) && (
          <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap', marginTop: 9 }}>
            {goal.current_state && (
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.4,
                  color: 'var(--text-muted)' }}>Where they started</div>
                <div style={{ fontSize: 13.5, lineHeight: 1.5 }}>{goal.current_state}</div>
              </div>
            )}
            {goal.target_text && (
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.4,
                  color: 'var(--text-muted)' }}>What success looks like</div>
                <div style={{ fontSize: 13.5, lineHeight: 1.5 }}>{goal.target_text}</div>
              </div>
            )}
          </div>
        )}

        {goal.notes && (
          <div style={{ fontSize: 13, lineHeight: 1.5, marginTop: 9, padding: '8px 10px',
            background: 'var(--cream)', borderRadius: 8, whiteSpace: 'pre-wrap' }}>
            {goal.notes}
          </div>
        )}

        {/* The numbers */}
        {isMilestone ? (
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 5 }}>
            {goal.target_date
              ? <>Target date <strong>{fmtDate(goal.target_date)}</strong>
                  {goal.status === 'active' && left !== null && (
                    <span style={{ color: overdue ? 'var(--red)' : 'var(--text-muted)' }}>
                      {' '}· {overdue ? `${Math.abs(left)} days past due` : `${left} days left`}
                    </span>
                  )}</>
              : 'No target date set'}
            {goal.achieved_on && <span style={{ color: 'var(--green)' }}> · achieved {fmtDate(goal.achieved_on)}</span>}
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 18, marginTop: 8, flexWrap: 'wrap' }}>
              {[
                { k: 'Starting point', v: goal.baseline ?? 'Unknown',
                  c: goal.baseline == null ? 'var(--text-muted)' : 'var(--text-primary)' },
                { k: 'Now', v: latest?.value ?? '—',
                  c: latest ? 'var(--teal)' : 'var(--text-muted)' },
                { k: 'Target', v: goal.target ?? '—', c: 'var(--navy)' },
              ].map(x => (
                <div key={x.k}>
                  <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.4,
                    color: 'var(--text-muted)' }}>{x.k}</div>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: 19, color: x.c }}>{x.v}</div>
                </div>
              ))}
              <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.4,
                  color: 'var(--text-muted)' }}>By when</div>
                <div style={{ fontSize: 12.5, color: overdue ? 'var(--red)' : 'var(--text-secondary)' }}>
                  {goal.target_date ? fmtDate(goal.target_date) : 'No date'}
                  {goal.status === 'active' && left !== null &&
                    ` · ${overdue ? `${Math.abs(left)}d over` : `${left}d left`}`}
                </div>
              </div>
            </div>

            {pct != null && (
              <div style={{ height: 7, background: 'var(--cream-dark)', borderRadius: 4,
                overflow: 'hidden', marginTop: 9 }}>
                <div style={{ width: `${pct}%`, height: '100%',
                  background: pct >= 100 ? 'var(--green)' : 'var(--teal)' }} />
              </div>
            )}
            {goal.baseline == null && !isMilestone && (
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 5, fontStyle: 'italic' }}>
                No starting point recorded — progress is measured from zero.
              </div>
            )}
            {goal.metric && (
              <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 4 }}>
                Measured by {goal.metric}
              </div>
            )}
          </>
        )}
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 6, padding: '8px 13px', flexWrap: 'wrap',
        borderTop: '1px solid var(--border-light)', background: 'var(--cream)' }}>
        {!isMilestone && goal.status === 'active' && (
          <button className="btn btn-ghost btn-sm" onClick={() => setTab(tab === 'number' ? null : 'number')}>
            <TrendingUp size={12} /> Record number
          </button>
        )}
        <button className="btn btn-ghost btn-sm" onClick={() => setTab(tab === 'note' ? null : 'note')}>
          <MessageCircle size={12} /> Add note
          {notes.length > 0 && <span style={{ color: 'var(--text-muted)' }}> ({notes.length})</span>}
        </button>
        {goal.status === 'active' && (
          <>
            <button className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }}
              onClick={() => onUpdate(goal.id, { status: 'met',
                closed_on: new Date().toISOString().slice(0, 10),
                achieved_on: isMilestone ? new Date().toISOString().slice(0, 10) : null })}>
              <Check size={12} /> {isMilestone ? 'Achieved' : 'Met'}
            </button>
            <button className="btn btn-ghost btn-sm"
              onClick={() => onUpdate(goal.id, { status: 'missed', closed_on: new Date().toISOString().slice(0, 10) })}>
              Missed
            </button>
          </>
        )}
        {goal.status !== 'active' && (
          <button className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }}
            onClick={() => onUpdate(goal.id, { status: 'active', closed_on: null, achieved_on: null })}>
            Reopen
          </button>
        )}
      </div>

      {/* Record a number */}
      {tab === 'number' && (
        <div style={{ padding: '11px 13px', borderTop: '1px solid var(--border-light)',
          display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ width: 110 }}>
            <label className="form-label">Value today</label>
            <input className="form-control" type="number" style={{ fontSize: 12.5 }} autoFocus
              value={val} onChange={e => setVal(e.target.value)} />
          </div>
          <div style={{ flex: 1, minWidth: 160 }}>
            <label className="form-label">Note <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional)</span></label>
            <input className="form-control" style={{ fontSize: 12.5 }}
              value={note} onChange={e => setNote(e.target.value)} />
          </div>
          <button className="btn btn-gold btn-sm" disabled={val === ''}
            onClick={() => {
              onCheckin({ goal_id: goal.id, value: Number(val), note: note.trim() || null, source: 'internal' })
              setVal(''); setNote(''); setTab(null)
            }}>Save</button>
        </div>
      )}

      {/* Add a note */}
      {tab === 'note' && (
        <div style={{ padding: '11px 13px', borderTop: '1px solid var(--border-light)' }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 7, flexWrap: 'wrap' }}>
            {Object.entries(NOTE_SOURCES).map(([k, v]) => (
              <button key={k} onClick={() => setSource(k)}
                style={{ fontSize: 11.5, padding: '3px 9px', borderRadius: 50, cursor: 'pointer',
                  border: `1px solid ${source === k ? v.color : 'var(--border)'}`,
                  background: source === k ? v.color : '#fff',
                  color: source === k ? '#fff' : 'var(--text-secondary)' }}>
                {v.label}
              </button>
            ))}
          </div>
          <textarea className="form-control" rows={2} autoFocus
            style={{ fontSize: 12.5, resize: 'vertical' }}
            value={note} onChange={e => setNote(e.target.value)}
            placeholder="What did they say about this goal? Their words, not a summary." />
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 7 }}>
            <button className="btn btn-gold btn-sm" disabled={!note.trim()}
              onClick={() => {
                onCheckin({ goal_id: goal.id, value: null, note: note.trim(), source })
                setNote(''); setTab(null)
              }}>Add note</button>
          </div>
        </div>
      )}

      {/* History — every note, in full. An AD reading this in a check-in
          should not have to click through to see what was said. */}
      {mine.length > 0 && (
        <div style={{ padding: '10px 13px', borderTop: '1px solid var(--border-light)' }}>
          {notes.length > 0 && (
            <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: 0.4, color: 'var(--text-muted)', marginBottom: 6 }}>
              Notes · {notes.length}
            </div>
          )}
          {numbers.length > 1 && (
            <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginBottom: 8 }}>
              {numbers.slice(0, 8).reverse().map(c => `${fmtDate(c.checked_on)}: ${c.value}`).join('  →  ')}
            </div>
          )}
          {notes.map(c => {
            const src = NOTE_SOURCES[c.source] || NOTE_SOURCES.internal
            return (
              <div key={c.id} style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: src.color, flexShrink: 0,
                  paddingTop: 2, width: 74 }}>{src.label}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12.5, whiteSpace: 'pre-wrap' }}>{c.note}</div>
                  <div style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>
                    {fmtDate(c.checked_on)}{c.value != null && ` · recorded ${c.value}`}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function GoalsPanel({ goals, checkins, onAdd, onUpdate, onCheckin }) {
  const [adding, setAdding] = useState(false)
  const [d, setD] = useState({ title: '', goal_type: 'number', metric: '',
    baseline: '', target: '', target_date: '' })

  const active = goals.filter(g => g.status === 'active')
  const closed = goals.filter(g => g.status !== 'active')
  const isMilestone = d.goal_type === 'milestone'

  return (
    <div className="card" style={{ marginBottom: 14, border: '1.5px solid var(--lightgreen)' }}>
      {/* Brand mint fill with the dark green on top — same pairing as
          .week-chip.done. White on this background is unreadable. */}
      <div className="card-header" style={{ background: 'var(--lightgreen)' }}>
        <span className="card-title" style={{ color: 'var(--green)' }}>
          <Target size={14} /> Goals
          {active.length > 0 && (
            <span style={{ fontWeight: 400, opacity: 0.75 }}> · {active.length} active</span>
          )}
        </span>
        <button className="btn btn-sm" onClick={() => setAdding(a => !a)}
          style={{ background: 'rgba(255,255,255,0.75)', color: 'var(--green)',
            border: '1px solid rgba(46,110,58,0.25)' }}>
          <Plus size={12} /> Add goal
        </button>
      </div>
      <div className="card-body">
        {/* Same shape as the delivery summary, so the two read as a pair */}
        {goals.length > 0 && (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
            {(() => {
              const met = goals.filter(g => g.status === 'met').length
              const missed = goals.filter(g => g.status === 'missed').length
              const closed = met + missed
              const overdueCount = active.filter(g =>
                g.target_date && new Date(g.target_date + 'T12:00:00') < new Date()).length
              const tracked = active.filter(g =>
                checkins.some(c => c.goal_id === g.id && c.value != null)).length
              return [
                { label: 'Hit rate', v: closed ? `${Math.round((met / closed) * 100)}%` : '—',
                  c: !closed ? 'var(--text-muted)'
                    : met / closed >= 0.7 ? 'var(--green)'
                    : met / closed >= 0.4 ? '#c4962a' : 'var(--red)' },
                { label: 'Active', v: active.length, c: 'var(--navy)' },
                { label: 'Being measured', v: `${tracked}/${active.length}`,
                  c: tracked < active.length ? '#c4962a' : 'var(--green)' },
                { label: 'Past due', v: overdueCount,
                  c: overdueCount ? 'var(--red)' : 'var(--text-muted)' },
              ]
            })().map(x => (
              <div key={x.label} style={{ flex: 1, minWidth: 100, padding: '9px 11px',
                borderRadius: 9, border: '1px solid var(--border-light)' }}>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: 21, color: x.c }}>{x.v}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{x.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* A goal nobody has recorded a number against isn't being watched */}
        {(() => {
          const untracked = active.filter(g =>
            g.goal_type !== 'milestone' &&
            !checkins.some(c => c.goal_id === g.id && c.value != null))
          if (!untracked.length) return null
          return (
            <div style={{ fontSize: 12.5, padding: '9px 11px', borderRadius: 8, marginBottom: 12,
              background: '#fff8e1', color: '#8a6000' }}>
              {untracked.length} goal{untracked.length === 1 ? ' has' : 's have'} no numbers
              recorded yet — nothing to show progress against at the next check-in.
            </div>
          )
        })()}

        {adding && (
          <div style={{ border: '1px dashed var(--border)', borderRadius: 9, padding: 13, marginBottom: 12 }}>
            <div className="form-group">
              <label className="form-label">What are we trying to achieve?</label>
              <input className="form-control" autoFocus value={d.title}
                onChange={e => setD({ ...d, title: e.target.value })}
                placeholder="New client inquiries per month" />
            </div>

            <div className="form-group">
              <label className="form-label">What kind of goal is it?</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {Object.entries(GOAL_TYPES).map(([k, v]) => (
                  <button key={k} onClick={() => setD({ ...d, goal_type: k })}
                    style={{ flex: 1, minWidth: 170, textAlign: 'left', cursor: 'pointer',
                      padding: '9px 11px', borderRadius: 8,
                      border: `1.5px solid ${d.goal_type === k ? 'var(--navy)' : 'var(--border)'}`,
                      background: d.goal_type === k ? '#eef2fa' : '#fff' }}>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--navy)' }}>{v.label}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{v.hint}</div>
                  </button>
                ))}
              </div>
            </div>

            {!isMilestone && (
              <>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">
                      Starting point <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(leave blank if unknown)</span>
                    </label>
                    <input className="form-control" type="number" value={d.baseline}
                      onChange={e => setD({ ...d, baseline: e.target.value })}
                      placeholder="Unknown" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Target</label>
                    <input className="form-control" type="number" value={d.target}
                      onChange={e => setD({ ...d, target: e.target.value })} />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">How will we measure it?</label>
                  <input className="form-control" value={d.metric}
                    onChange={e => setD({ ...d, metric: e.target.value })}
                    placeholder="Call tracking, form fills, booked consults" />
                </div>
              </>
            )}

            <div className="form-group">
              <label className="form-label">
                By when {isMilestone ? '' : <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional)</span>}
              </label>
              <input className="form-control" type="date" value={d.target_date}
                onChange={e => setD({ ...d, target_date: e.target.value })} />
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setAdding(false)}>Cancel</button>
              <button className="btn btn-gold btn-sm" disabled={!d.title.trim()}
                onClick={async () => {
                  const ok = await onAdd({
                    title: d.title.trim(),
                    goal_type: d.goal_type,
                    metric: isMilestone ? null : (d.metric.trim() || null),
                    baseline: isMilestone || d.baseline === '' ? null : Number(d.baseline),
                    target: isMilestone || d.target === '' ? null : Number(d.target),
                    target_date: d.target_date || null,
                    is_primary: goals.length === 0,
                  })
                  if (ok !== false) {
                    setD({ title: '', goal_type: 'number', metric: '', baseline: '', target: '', target_date: '' })
                    setAdding(false)
                  }
                }}>Add goal</button>
            </div>
          </div>
        )}

        {active.length === 0 && !adding && (
          <div style={{ fontSize: 12.5, color: '#8a6000', padding: '10px 12px',
            background: '#fff8e1', borderRadius: 8 }}>
            No goals yet. Every touchpoint measures against these — without one there is
            nothing to check progress against at day 30, 60, or 90.
          </div>
        )}

        {active.map(g => (
          <GoalCard key={g.id} goal={g} checkins={checkins} onUpdate={onUpdate} onCheckin={onCheckin} />
        ))}

        {closed.length > 0 && (
          <>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: 0.4, color: 'var(--text-muted)', margin: '14px 0 8px' }}>
              Closed · {closed.length}
            </div>
            {closed.map(g => (
              <GoalCard key={g.id} goal={g} checkins={checkins} onUpdate={onUpdate} onCheckin={onCheckin} />
            ))}
          </>
        )}
      </div>
    </div>
  )
}

// ── Commitments ─────────────────────────────────────────────────────────────
// What we promised, and whether we hit it. Different from tasks: a task is
// internal work, a commitment is something the client is waiting on.
function CommitmentsPanel({ rows, onAdd, onUpdate, notes, onAddNote }) {
  const [adding, setAdding] = useState(false)
  const [d, setD] = useState({ what: '', due_date: '', source: 'kickoff' })
  const [openId, setOpenId] = useState(null)
  const [noteDraft, setNoteDraft] = useState({ body: '', source: 'internal' })
  const st = commitmentStats(rows)
  const today = new Date().toISOString().slice(0, 10)

  const sorted = [...rows].sort((a, b) => {
    if ((a.status === 'open') !== (b.status === 'open')) return a.status === 'open' ? -1 : 1
    return a.due_date.localeCompare(b.due_date)
  })

  return (
    <div className="card" style={{ marginTop: 14, border: '1.5px solid var(--teal)' }}>
      <div className="card-header" style={{ background: 'var(--teal)' }}>
        <span className="card-title" style={{ color: '#fff' }}>
          <Handshake size={14} /> What we're delivering</span>
        <button className="btn btn-sm" onClick={() => setAdding(a => !a)}
          style={{ background: 'rgba(255,255,255,0.18)', color: '#fff', border: 'none' }}>
          <Plus size={12} /> Add
        </button>
      </div>
      <div className="card-body">
        {rows.length > 0 && (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
            {[
              { label: 'Hit rate', v: st.hitRate == null ? '—' : `${Math.round(st.hitRate)}%`,
                c: st.hitRate == null ? 'var(--text-muted)' : st.hitRate >= 90 ? 'var(--green)'
                  : st.hitRate >= 70 ? '#c4962a' : 'var(--red)' },
              { label: 'Open', v: st.open, c: 'var(--navy)' },
              { label: 'Overdue', v: st.overdue, c: st.overdue ? 'var(--red)' : 'var(--text-muted)' },
              { label: 'Missed', v: st.missed, c: st.missed ? 'var(--red)' : 'var(--text-muted)' },
            ].map(x => (
              <div key={x.label} style={{ flex: 1, minWidth: 100, padding: '9px 11px',
                borderRadius: 9, border: '1px solid var(--border-light)' }}>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: 21, color: x.c }}>{x.v}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{x.label}</div>
              </div>
            ))}
          </div>
        )}

        {st.silent > 0 && (
          <div style={{ fontSize: 12.5, padding: '9px 11px', borderRadius: 8, marginBottom: 10,
            background: '#fff8e1', color: '#8a6000' }}>
            {st.silent} commitment{st.silent === 1 ? '' : 's'} delivered but the client was never
            told. Delivered silently earns nothing — tick "told them" once you have.
          </div>
        )}

        {adding && (
          <div style={{ border: '1px dashed var(--border)', borderRadius: 9, padding: 12, marginBottom: 10 }}>
            <div className="form-group">
              <label className="form-label">What did we promise?</label>
              <input className="form-control" value={d.what}
                onChange={e => setD({ ...d, what: e.target.value })}
                placeholder="New homepage live" />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">By when</label>
                <input className="form-control" type="date" value={d.due_date}
                  onChange={e => setD({ ...d, due_date: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Where was it promised?</label>
                <select className="form-control" value={d.source}
                  onChange={e => setD({ ...d, source: e.target.value })}>
                  {Object.entries(COMMITMENT_SOURCE).map(([k, v]) =>
                    <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setAdding(false)}>Cancel</button>
              <button className="btn btn-gold btn-sm" disabled={!d.what.trim() || !d.due_date}
                onClick={async () => {
                  const ok = await onAdd({ what: d.what.trim(), due_date: d.due_date, source: d.source })
                  if (ok !== false) { setD({ what: '', due_date: '', source: 'kickoff' }); setAdding(false) }
                }}>Add</button>
            </div>
          </div>
        )}

        {rows.length === 0 && !adding && (
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)', fontStyle: 'italic' }}>
            Nothing recorded. Add the dates promised at the sales call and kickoff — those are
            the ones clients remember.
          </div>
        )}

        {sorted.map(r => {
          const late = r.status === 'open' && r.due_date < today
          const s2 = COMMITMENT_STATUS[r.status]
          const mine = (notes || []).filter(n => n.commitment_id === r.id)
          return (
            <div key={r.id}>
            <div
              onMouseEnter={e => e.currentTarget.style.background = 'var(--cream)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              style={{ display: 'flex', alignItems: 'center', gap: 9, borderRadius: 8,
                padding: '9px 10px', margin: '0 -10px',
                borderBottom: '1px solid var(--border-light)', flexWrap: 'wrap',
                transition: 'background .12s' }}>
              <div style={{ flex: 1, minWidth: 180 }}>
                <div style={{ fontSize: 13 }}>{r.what}</div>
                <div style={{ fontSize: 11.5, color: late ? 'var(--red)' : 'var(--text-muted)' }}>
                  due {fmtDate(r.due_date)}
                  {late && ` · ${Math.floor((Date.now() - new Date(r.due_date + 'T12:00:00')) / 86400000)} days late`}
                  {r.source && ` · promised at ${COMMITMENT_SOURCE[r.source]?.toLowerCase()}`}
                </div>
              </div>
              <span style={{ fontSize: 11, fontWeight: 600, color: s2.color }}>{s2.label}</span>
              {r.status === 'open' && (
                <>
                  <button className="btn btn-ghost btn-sm"
                    onClick={() => onUpdate(r.id, { status: 'met', delivered_on: today })}>Met</button>
                  <button className="btn btn-ghost btn-sm"
                    onClick={() => onUpdate(r.id, { status: 'missed' })}>Missed</button>
                </>
              )}
              {r.status === 'met' && !r.client_told && (
                <button className="btn btn-ghost btn-sm"
                  onClick={() => onUpdate(r.id, { client_told: true })}>
                  <Send size={11} /> Told them
                </button>
              )}
              <button className="btn btn-ghost btn-sm"
                onClick={() => setOpenId(openId === r.id ? null : r.id)}>
                <MessageCircle size={11} />
                {mine.length > 0 ? ` ${mine.length}` : ''}
              </button>
            </div>

            {openId === r.id && (
              <div style={{ padding: '10px 12px', margin: '0 -10px 8px',
                background: 'var(--cream)', borderRadius: 8 }}>
                {mine.map(n => {
                  const src = NOTE_SOURCES[n.source] || NOTE_SOURCES.internal
                  return (
                    <div key={n.id} style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                      <span style={{ fontSize: 10, fontWeight: 700, color: src.color,
                        width: 74, flexShrink: 0, paddingTop: 2 }}>{src.label}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 12.5, whiteSpace: 'pre-wrap' }}>{n.body}</div>
                        <div style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>
                          {fmtDate(n.created_at)}
                        </div>
                      </div>
                    </div>
                  )
                })}
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
                  {Object.entries(NOTE_SOURCES).map(([k, v]) => (
                    <button key={k} onClick={() => setNoteDraft({ ...noteDraft, source: k })}
                      style={{ fontSize: 11, padding: '2px 8px', borderRadius: 50, cursor: 'pointer',
                        border: `1px solid ${noteDraft.source === k ? v.color : 'var(--border)'}`,
                        background: noteDraft.source === k ? v.color : '#fff',
                        color: noteDraft.source === k ? '#fff' : 'var(--text-secondary)' }}>
                      {v.label}
                    </button>
                  ))}
                </div>
                <textarea className="form-control" rows={2}
                  style={{ fontSize: 12.5, resize: 'vertical' }}
                  value={noteDraft.body}
                  onChange={e => setNoteDraft({ ...noteDraft, body: e.target.value })}
                  placeholder="Why it moved, what they said, what changed." />
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
                  <button className="btn btn-gold btn-sm" disabled={!noteDraft.body.trim()}
                    onClick={async () => {
                      await onAddNote(r.id, { body: noteDraft.body.trim(), source: noteDraft.source })
                      setNoteDraft({ body: '', source: 'internal' })
                    }}>Add note</button>
                </div>
              </div>
            )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// Where is this client, right now? Multi-select, because the combination is
// what tells you what to do — "behind on delivery" plus "questioning cost" is
// a different situation from either on its own.
function StatusPanel({ goal, onSave }) {
  const flags = goal?.flags || []
  const [focus, setFocus] = useState('')
  useEffect(() => { setFocus(goal?.focus || '') }, [goal?.focus])

  const toggle = (key) => {
    const next = flags.includes(key) ? flags.filter(f => f !== key) : [...flags, key]
    onSave({ flags: next, flags_updated_on: new Date().toISOString().slice(0, 10) })
  }

  const { urgent } = flagSummary(flags)
  const actions = flags.map(getFlag).filter(Boolean).filter(f => f.group !== 'good')
  const stale = goal?.flags_updated_on
    ? Math.floor((Date.now() - new Date(goal.flags_updated_on + 'T12:00:00')) / 86400000)
    : null

  return (
    <div className="card" style={{ marginTop: 14,
      border: urgent.length ? '1.5px solid var(--red)' : '1px solid var(--border-light)' }}>
      <div className="card-header" style={{ background: urgent.length ? 'var(--red-bg)' : undefined }}>
        <span className="card-title" style={{ color: urgent.length ? 'var(--red)' : undefined }}>
          <MessageCircle size={13} /> Where they are
        </span>
        <span style={{ fontSize: 11.5,
          color: stale === null || stale > REVIEW_CADENCE_DAYS + 14 ? 'var(--red)' : 'var(--text-muted)' }}>
          {stale === null ? 'never reviewed'
            : stale === 0 ? 'reviewed today'
            : `reviewed ${stale}d ago`}
          {stale !== null && stale > REVIEW_CADENCE_DAYS + 14 && ' — overdue for a check-in'}
        </span>
      </div>
      <div className="card-body">

        {/* What to ask the AD. Keeps the check-in on the things that predict
            churn instead of a general "how are they doing". */}
        <div style={{ marginBottom: 14, padding: '10px 12px', borderRadius: 9,
          background: 'var(--cream)' }}>
          <div style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: 0.4, color: 'var(--text-muted)', marginBottom: 6 }}>
            Ask the AD
          </div>
          {REVIEW_PROMPTS.map(p => (
            <div key={p.group} style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
              <span style={{ fontSize: 10.5, fontWeight: 700, color: FLAG_GROUPS[p.group].color,
                width: 92, flexShrink: 0, paddingTop: 1 }}>
                {FLAG_GROUPS[p.group].label}
              </span>
              <span style={{ fontSize: 12.5, color: 'var(--text-primary)' }}>{p.ask}</span>
            </div>
          ))}
        </div>

        {/* Pick as many as apply */}
        {Object.entries(FLAG_GROUPS).map(([gk, g]) => {
          const inGroup = FLAGS.filter(f => f.group === gk)
          return (
            <div key={gk} style={{ marginBottom: 11 }}>
              <div style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase',
                letterSpacing: 0.4, color: g.color, marginBottom: 5 }}>{g.label}</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {inGroup.map(f => {
                  const on = flags.includes(f.key)
                  return (
                    <button key={f.key} onClick={() => toggle(f.key)} title={f.then}
                      style={{ fontSize: 12, padding: '4px 10px', borderRadius: 50, cursor: 'pointer',
                        border: `1px solid ${on ? g.color : 'var(--border)'}`,
                        background: on ? g.color : '#fff',
                        color: on ? '#fff' : 'var(--text-secondary)',
                        fontWeight: on ? 600 : 400 }}>
                      {f.label}
                    </button>
                  )
                })}
              </div>
            </div>
          )
        })}

        {/* What each selected flag implies — the pre-decided response */}
        {actions.length > 0 && (
          <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--border-light)' }}>
            <div style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: 0.4, color: 'var(--text-muted)', marginBottom: 7 }}>
              So what do we do
            </div>
            {actions.map(f => {
              const g = FLAG_GROUPS[f.group]
              return (
                <div key={f.key} style={{ display: 'flex', gap: 9, marginBottom: 6,
                  padding: '7px 10px', borderRadius: 8, background: g.bg }}>
                  <span style={{ fontSize: 11.5, fontWeight: 700, color: g.color,
                    flexShrink: 0, width: 150 }}>{f.label}</span>
                  <span style={{ fontSize: 12.5, color: 'var(--text-primary)' }}>{f.then}</span>
                </div>
              )
            })}
          </div>
        )}

        {/* One line: the thing to actually work on */}
        <div className="form-group" style={{ marginTop: 14, marginBottom: 0 }}>
          <label className="form-label">Focus right now</label>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>
            One sentence. What are we trying to change about this relationship this month?
          </div>
          <input className="form-control" value={focus} onChange={e => setFocus(e.target.value)}
            onBlur={() => { if (focus !== (goal?.focus || '')) onSave({ focus: focus || null }) }}
            placeholder="Get them to see the SEO work that's already done." />
        </div>

        {/* Marks the review as done today. This comes from the monthly AD
            check-in — Tori isn't speaking to these clients, the AD is. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14,
          paddingTop: 12, borderTop: '1px solid var(--border-light)', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: 'var(--text-muted)', flex: 1, minWidth: 190 }}>
            Set in the monthly check-in with the AD. Every {REVIEW_CADENCE_DAYS} days.
          </span>
          <button className="btn btn-ghost btn-sm"
            onClick={() => onSave({ flags_updated_on: new Date().toISOString().slice(0, 10) })}>
            <Check size={12} /> Reviewed today
          </button>
        </div>
      </div>
    </div>
  )
}

// A client flagged on the AD scorecard's watchlist shows the concern here too,
// so whoever opens this page knows before they plan anything.
function WatchBanner({ entry }) {
  if (!entry) return null
  const LABELS = {
    bad_data: 'Numbers are poor', complaining: 'They have complained',
    concerned: 'We are concerned', stagnant: 'Stagnant — needs something new',
    at_risk: 'At risk of leaving', relationship: 'Relationship needs repair',
    delivery: 'We are behind', other: 'Flagged',
  }
  const urgent = entry.severity === 'urgent'
  return (
    <div style={{ padding: '11px 13px', borderRadius: 10, marginBottom: 14,
      background: urgent ? 'var(--red-bg)' : 'var(--blush)',
      border: `1.5px solid ${urgent ? 'var(--red)' : 'var(--mauve)'}` }}>
      <div style={{ fontSize: 12.5, fontWeight: 700,
        color: urgent ? 'var(--red)' : 'var(--mauve)' }}>
        👁 On the watchlist — {LABELS[entry.reason] || entry.reason}
      </div>
      {entry.reason_note && (
        <div style={{ fontSize: 12.5, marginTop: 4 }}>{entry.reason_note}</div>
      )}
      {entry.what_good_looks_like && (
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
          Back on track means: {entry.what_good_looks_like}
        </div>
      )}
      <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 5 }}>
        Raised in the AD check-in. To-dos and status live on the AD Scorecard — work the
        plan there, not twice.
      </div>
    </div>
  )
}

// What was said in the kickoff meeting. Read-only here — it's edited in the
// Kickoff Decks section, and having two places to edit one note is how they
// end up disagreeing.
function KickoffMeetingNotes({ decks }) {
  const withNotes = decks.filter(d => d.consultation_notes)
  if (!decks.length) return null

  return (
    <div className="card" style={{ marginBottom: 14 }}>
      <div className="card-header">
        <span className="card-title">From the kickoff meeting</span>
        <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
          edited in Kickoff Decks
        </span>
      </div>
      <div className="card-body">
        {withNotes.length === 0 ? (
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)', fontStyle: 'italic' }}>
            A deck exists but no notes were taken during the meeting.
          </div>
        ) : withNotes.map(d => (
          <div key={d.id} style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 3 }}>
              {d.meeting_date ? fmtDate(d.meeting_date) : 'No date'}
            </div>
            <div style={{ fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap',
              padding: '10px 12px', background: 'var(--cream)', borderRadius: 8 }}>
              {d.consultation_notes}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// Graduating is a moment worth marking: it's the last time anyone will look
// closely at this client for a while, and the last chance to say so out loud.
function GraduateModal({ client, phase, openCount, onClose, onConfirm }) {
  const [wins, setWins] = useState('')
  const [note, setNote] = useState('')
  const next = PHASES[phase].next

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <span className="modal-title">
            <PartyPopper size={15} /> {client.name} → {PHASES[next].label}
          </span>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          {openCount > 0 && (
            <div style={{ fontSize: 12.5, padding: '9px 11px', borderRadius: 8, marginBottom: 12,
              background: '#fff8e1', color: '#8a6000' }}>
              {openCount} touchpoint{openCount === 1 ? '' : 's'} still open. You can move them
              anyway, but those will stay unfinished.
            </div>
          )}

          <div className="form-group">
            <label className="form-label">What went well?</label>
            <textarea className="form-control" rows={3} value={wins}
              onChange={e => setWins(e.target.value)}
              style={{ fontSize: 12.5, resize: 'vertical' }}
              placeholder="Worth writing now while it's fresh — this feeds the AD handoff and gives you something concrete for the next kickoff." />
          </div>

          <div className="form-group">
            <label className="form-label">Anything the AD should know</label>
            <textarea className="form-control" rows={2} value={note}
              onChange={e => setNote(e.target.value)}
              style={{ fontSize: 12.5, resize: 'vertical' }}
              placeholder="Sensitivities, who to copy, what they keep asking about." />
          </div>

          {next === 'graduated' && (
            <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', padding: '10px 12px',
              background: 'var(--cream)', borderRadius: 8 }}>
              <strong>After this:</strong> they'll show in Graduated for 30 days as a reminder to
              send a proper sign-off note, then drop off the list.
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-gold" onClick={() => onConfirm(wins.trim(), note.trim())}>
            <GraduationCap size={13} /> Move to {PHASES[next].label}
          </button>
        </div>
      </div>
    </div>
  )
}

// The goodbye list: recently graduated clients nobody has signed off with yet.
function GoodbyeBand({ rows, onMarkSent }) {
  if (rows.length === 0) return null
  return (
    <div className="card" style={{ marginBottom: 14, border: '1.5px solid var(--gold)' }}>
      <div className="card-header" style={{ background: '#fdf6e3' }}>
        <span className="card-title" style={{ color: 'var(--gold)' }}>
          <PartyPopper size={13} /> Just graduated — say goodbye
        </span>
        <span style={{ fontSize: 12, color: 'var(--gold)' }}>{rows.length}</span>
      </div>
      <div className="card-body">
        <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginBottom: 10 }}>
          They've finished the structured onboarding. A short note marking that — what was
          achieved, who they'll be working with now — costs nothing and is the last impression
          of a process they just spent six months inside.
        </div>
        {rows.map(({ client, goal, days }) => (
          <div key={client.id} style={{ display: 'flex', alignItems: 'center', gap: 10,
            padding: '8px 0', borderTop: '1px solid var(--border-light)', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 180 }}>
              <div style={{ fontSize: 13, fontWeight: 500 }}>{client.name}</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
                graduated {days === 0 ? 'today' : `${days} day${days === 1 ? '' : 's'} ago`}
                {goal?.graduation_wins && ` · ${goal.graduation_wins.slice(0, 70)}${goal.graduation_wins.length > 70 ? '…' : ''}`}
              </div>
            </div>
            <button className="btn btn-ghost btn-sm" onClick={() => onMarkSent(client.id)}>
              <Check size={12} /> Sent
            </button>
          </div>
        ))}
      </div>
    </div>
  )
}

export default function First90View({ allClients, tasks, profiles, currentUser, onOpenTasks }) {
  const toast = useToast()
  const [touchpoints, setTouchpoints] = useState([])
  const [goals, setGoals] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedId, setSelectedId] = useState(null)
  const [expanded, setExpanded] = useState(null)
  const [generating, setGenerating] = useState(false)
  const [phaseTab, setPhaseTab] = useState('onboarding')
  const [queueOpen, setQueueOpen] = useState(true)
  const [checkins, setCheckins] = useState([])
  const [clientGoals, setClientGoals] = useState([])
  const [commitments, setCommitments] = useState([])
  const [gradModal, setGradModal] = useState(null)
  const [watch, setWatch] = useState([])
  const [decks, setDecks] = useState([])
  const [cNotes, setCNotes] = useState([])

  const load = useCallback(async () => {
    setLoading(true)
    const [tpRes, gRes, ciRes, cgRes, ccRes, wRes, dkRes, cnRes] = await Promise.all([
      supabase.from('touchpoints').select('*').order('due_date'),
      supabase.from('client_90day').select('*'),
      supabase.from('goal_checkins').select('*').order('checked_on', { ascending: false }),
      supabase.from('client_goals').select('*').order('sort_order'),
      supabase.from('client_commitments').select('*').order('due_date'),
      // Open watchlist entries — a client flagged in the AD check-in shows the
      // concern here too, so the same problem isn't worked in two places.
      supabase.from('watchlist').select('*').in('status', ['open', 'improving']),
      // Notes taken during the kickoff meeting live on the deck, not on the
      // 90-day record — surface them here so they're read where they matter.
      supabase.from('kickoff_decks').select('*').order('created_at', { ascending: false }),
      supabase.from('commitment_notes').select('*').order('created_at', { ascending: false }),
    ])
    if (tpRes.error) toast('Could not load touchpoints: ' + tpRes.error.message, 'error')
    setTouchpoints(tpRes.data || [])
    setGoals(gRes.data || [])
    setCheckins(ciRes.error ? [] : (ciRes.data || []))
    setClientGoals(cgRes.error ? [] : (cgRes.data || []))
    setCommitments(ccRes.error ? [] : (ccRes.data || []))
    setWatch(wRes.error ? [] : (wRes.data || []))
    setDecks(dkRes.error ? [] : (dkRes.data || []))
    setCNotes(cnRes.error ? [] : (cnRes.data || []))
    setLoading(false)
  }, [toast])

  useEffect(() => { load() }, [load])

  // Clients inside their first 90 days, soonest-due first.
  const newClients = useMemo(() => {
    return allClients
      .filter(c => !c.archived_at)
      .map(c => {
        const day = dayNumber(c.start_date || c.created_at)
        const tps = touchpoints.filter(t => t.client_id === c.id)
        const open = tps.filter(t => !t.completed_at && !t.skipped)
        const overdue = open.filter(t => touchpointState(t) === 'overdue')
        const next = open.length ? open.reduce((a, b) => a.due_date < b.due_date ? a : b) : null
        const g = goals.find(x => x.client_id === c.id)
        return { client: c, day, tps, open, overdue, next, phase: g?.phase, goal: g }
      })
      // Phase drives the list now, not a day window — so nobody vanishes at
      // day 100. A client with no 90-day row yet is treated as onboarding if
      // they're inside 90 days.
      .filter(x => {
        // A client who starts next week should be visible — that's when you
        // prepare, not after they've already begun.
        if (x.day === null || x.day < -30) return false
        const phase = x.phase || (x.day <= 90 ? 'onboarding' : x.day <= 180 ? 'establishing' : 'graduated')
        return phase === phaseTab
      })
      .sort((a, b) => {
        // Anything flagged urgent outranks a merely overdue touchpoint.
        const au = flagSummary(a.goal?.flags || []).urgent.length
        const bu = flagSummary(b.goal?.flags || []).urgent.length
        if (au !== bu) return bu - au
        if (a.overdue.length !== b.overdue.length) return b.overdue.length - a.overdue.length
        if (!a.next) return 1
        if (!b.next) return -1
        return a.next.due_date.localeCompare(b.next.due_date)
      })
  }, [allClients, touchpoints, goals, phaseTab])

  useEffect(() => {
    if (newClients.length && !newClients.some(x => x.client.id === selectedId)) {
      setSelectedId(newClients[0].client.id)
    }
    if (!newClients.length) setSelectedId(null)
  }, [newClients, selectedId])

  const selected = newClients.find(x => x.client.id === selectedId)
  const goal = goals.find(g => g.client_id === selectedId)

  // Only these columns are written. Spreading the whole draft used to send back
  // read-only fields too, which the database rejects — and the rejection was
  // easy to miss.
  const GOAL_FIELDS = [
    'too_slow_looks_like', 'tried_before', 'other_influencers', 'monthly_ceiling',
    'promised_off_contract',
  ]

  const saveGoal = async (draft) => {
    if (!selectedId) { toast('No client selected', 'error'); return false }
    const payload = { client_id: selectedId, updated_by: currentUser?.id || null,
      updated_at: new Date().toISOString() }
    GOAL_FIELDS.forEach(k => {
      if (draft[k] !== undefined) payload[k] = draft[k] === '' ? null : draft[k]
    })

    const { data, error } = await supabase
      .from('client_90day')
      .upsert(payload, { onConflict: 'client_id' })
      .select()

    if (error) {
      console.error('client_90day upsert failed', { payload, error })
      toast('Could not save: ' + error.message, 'error')
      return false
    }
    if (!data || data.length === 0) {
      // No error but no row back means row-level security blocked the write.
      toast('Save was blocked — you may not have access to this client', 'error')
      return false
    }
    toast('Goal saved', 'success')
    await load()
    return true
  }

  // Writes the flag / focus / contact fields. Kept separate from the kickoff
  // notes save so a toggle can't clobber a text field mid-edit.
  const saveRelationship = async (fields) => {
    const { error } = await supabase.from('client_90day').upsert({
      client_id: selectedId, ...fields, updated_by: currentUser?.id || null,
      updated_at: new Date().toISOString(),
    }, { onConflict: 'client_id' })
    if (error) { toast('Could not save: ' + error.message, 'error'); return }
    load()
  }

  const addCheckin = async (fields) => {
    const { error } = await supabase.from('goal_checkins').insert({
      client_id: selectedId, ...fields, recorded_by: currentUser?.id || null,
    })
    if (error) { toast('Could not save: ' + error.message, 'error'); return }
    toast('Check-in recorded', 'success')
    load()
  }

  const addGoal = async (fields) => {
    const { error } = await supabase.from('client_goals')
      .insert({ client_id: selectedId, created_by: currentUser?.id || null, ...fields })
    if (error) { toast('Could not add goal: ' + error.message, 'error'); return false }
    toast('Goal added', 'success'); await load(); return true
  }
  const updateGoal = async (id, fields) => {
    const { error } = await supabase.from('client_goals').update(fields).eq('id', id)
    if (error) { toast('Could not update: ' + error.message, 'error'); return }
    load()
  }
  const addCommitment = async (fields) => {
    const { error } = await supabase.from('client_commitments')
      .insert({ client_id: selectedId, promised_by: currentUser?.id || null, ...fields })
    if (error) { toast('Could not add: ' + error.message, 'error'); return false }
    toast('Commitment recorded', 'success'); await load(); return true
  }
  const addCommitmentNote = async (commitmentId, fields) => {
    const c = commitments.find(x => x.id === commitmentId)
    const { error } = await supabase.from('commitment_notes').insert({
      commitment_id: commitmentId, client_id: c?.client_id || selectedId,
      author_id: currentUser?.id || null, ...fields,
    })
    if (error) { toast('Could not add note: ' + error.message, 'error'); return }
    load()
  }

  const updateCommitment = async (id, fields) => {
    const { error } = await supabase.from('client_commitments').update(fields).eq('id', id)
    if (error) { toast('Could not update: ' + error.message, 'error'); return }
    load()
  }
  const markGoodbyeSent = async (clientId) => {
    const { error } = await supabase.from('client_90day').upsert({
      client_id: clientId, goodbye_sent: true,
      goodbye_sent_on: new Date().toISOString().slice(0, 10),
    }, { onConflict: 'client_id' })
    if (error) { toast('Could not save: ' + error.message, 'error'); return }
    toast('Nice one', 'success'); load()
  }

  const graduate = async (clientId, wins, note) => {
    if (wins || note) {
      await supabase.from('client_90day').upsert({
        client_id: clientId,
        graduation_wins: wins || null,
        graduation_note: note || null,
      }, { onConflict: 'client_id' })
    }
    const { data, error } = await supabase.rpc('graduate_client', { p_client_id: clientId })
    if (error) { toast('Could not graduate: ' + error.message, 'error'); return }
    toast(`🎉 ${PHASES[data]?.label || data}`, 'success')
    setGradModal(null)
    load()
  }

  const completeTouchpoint = async (tp, note, health) => {
    const { error } = await supabase.from('touchpoints').update({
      completed_at: new Date().toISOString(),
      completed_by: currentUser?.id || null,
      notes: note?.trim() || null,
    }).eq('id', tp.id)
    if (error) { toast('Could not save: ' + error.message, 'error'); return }

    if (health) {
      await supabase.from('client_90day').upsert({
        client_id: tp.client_id, health_status: health,
        health_set_at: new Date().toISOString(), updated_by: currentUser?.id || null,
      }, { onConflict: 'client_id' })
    }
    toast('Marked done', 'success')
    setExpanded(null)
    load()
  }

  const skipTouchpoint = async (tp) => {
    const { error } = await supabase.from('touchpoints')
      .update({ skipped: true, completed_at: new Date().toISOString() }).eq('id', tp.id)
    if (error) { toast('Could not save: ' + error.message, 'error'); return }
    setExpanded(null)
    load()
  }

  // For clients added before this section existed.
  const generateFor = async (clientId) => {
    setGenerating(true)
    const { error } = await supabase.rpc('generate_touchpoints', { p_client_id: clientId })
    setGenerating(false)
    if (error) { toast('Could not generate: ' + error.message, 'error'); return }
    toast('Touchpoints created', 'success')
    load()
  }

  // Counts for the tab badges, computed across every client rather than the
  // currently-filtered list.
  const phaseCounts = useMemo(() => {
    const out = {}
    allClients.filter(c => !c.archived_at).forEach(c => {
      const day = dayNumber(c.start_date || c.created_at)
      if (day === null || day < 0) return
      const g = goals.find(x => x.client_id === c.id)
      const phase = g?.phase || (day <= 90 ? 'onboarding' : day <= 180 ? 'establishing' : 'graduated')
      if (phase === 'graduated' && day > 365) return   // stop counting forever
      out[phase] = (out[phase] || 0) + 1
    })
    return out
  }, [allClients, goals])

  // Graduated in the last 30 days without a sign-off note sent.
  const goodbyeList = useMemo(() => {
    return allClients
      .filter(c => !c.archived_at)
      .map(c => {
        const g = goals.find(x => x.client_id === c.id)
        if (!g || g.phase !== 'graduated' || !g.graduated_at || g.goodbye_sent) return null
        const days = Math.floor((Date.now() - new Date(g.graduated_at).getTime()) / 86400000)
        return days <= 30 ? { client: c, goal: g, days } : null
      })
      .filter(Boolean)
      .sort((a, b) => a.days - b.days)
  }, [allClients, goals])

  if (loading) return <div style={{ padding: 40, color: 'var(--text-muted)' }}>Loading…</div>

  const totalOverdue = newClients.reduce((n, x) => n + x.overdue.length, 0)

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 12, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, color: 'var(--navy)' }}>
          🌱 New Client Journey
          <span style={{ fontSize: 14, color: 'var(--text-muted)', fontWeight: 400 }}>
            {' '}· {newClients.length} in {PHASES[phaseTab].label.toLowerCase()}
            {totalOverdue > 0 && <span style={{ color: 'var(--red)' }}> · {totalOverdue} overdue</span>}
          </span>
        </div>
      </div>

      {/* Phase tabs — a client moves through these rather than dropping off a list */}
      <div className="tabs" style={{ marginBottom: 14 }}>
        {Object.entries(PHASES).filter(([k]) => k !== 'paused').map(([k, v]) => {
          const n = phaseCounts[k] || 0
          return (
            <button key={k} className={`tab ${phaseTab === k ? 'active' : ''}`}
              onClick={() => setPhaseTab(k)}>
              {v.label}
              <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 5 }}>{v.days}</span>
              {n > 0 && <span style={{ marginLeft: 6, background: v.color, color: '#fff',
                borderRadius: 10, padding: '1px 6px', fontSize: 11, fontWeight: 700 }}>{n}</span>}
            </button>
          )
        })}
      </div>

      <GoodbyeBand rows={goodbyeList} onMarkSent={markGoodbyeSent} />

      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {/* Queue — collapsible, so the detail can take the full width */}
        {!queueOpen ? (
          <button onClick={() => setQueueOpen(true)} title="Show the client queue"
            className="card"
            style={{ width: 40, flexShrink: 0, padding: '14px 0', cursor: 'pointer',
              border: '1px solid var(--border-light)', background: '#fff',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            <ChevronRight size={15} style={{ color: 'var(--text-muted)' }} />
            <span style={{ writingMode: 'vertical-rl', fontSize: 11.5, fontWeight: 600,
              color: 'var(--text-muted)', letterSpacing: 0.5 }}>
              Queue · {newClients.length}
            </span>
            {totalOverdue > 0 && (
              <span style={{ fontSize: 10, background: 'var(--red)', color: '#fff',
                borderRadius: 10, padding: '1px 5px', fontWeight: 700 }}>{totalOverdue}</span>
            )}
          </button>
        ) : (
        <div className="card" style={{ width: 268, flexShrink: 0 }}>
          <div className="card-header">
            <span className="card-title">Queue</span>
            <button className="btn-icon" title="Hide the queue"
              onClick={() => setQueueOpen(false)}>
              <ChevronLeft size={14} />
            </button>
          </div>
          <div style={{ padding: 8 }}>
            {newClients.length === 0 && (
              <div style={{ fontSize: 12.5, color: 'var(--text-muted)', fontStyle: 'italic', padding: 10 }}>
                No clients in their first 90 days.
              </div>
            )}
            {newClients.map(({ client, day, next, overdue, goal: g }) => {
              const active = client.id === selectedId
              const st = next ? touchpointState(next) : 'done'
              const flagList = (g?.flags || []).map(getFlag).filter(Boolean)
              const watched = watch.some(w => w.client_id === client.id)
              const urgentCount = flagSummary(g?.flags || []).urgent.length
              return (
                <div key={client.id} onClick={() => { setSelectedId(client.id); setExpanded(null) }}
                  style={{ padding: '10px 12px', borderRadius: 8, cursor: 'pointer', marginBottom: 4,
                    background: active ? 'var(--navy)' : 'transparent',
                    color: active ? '#fff' : 'var(--text-primary)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6 }}>
                    <div style={{ fontSize: 13, fontWeight: active ? 600 : 500, overflow: 'hidden',
                      textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {watched && <span title="On the AD watchlist">👁 </span>}{client.name}
                    </div>
                    {urgentCount > 0 && !active &&
                      <span title="Needs attention" style={{ width: 7, height: 7, borderRadius: 50,
                        background: 'var(--red)', flexShrink: 0, marginTop: 5 }} />}
                    {overdue.length > 0 && !active &&
                      <span style={{ fontSize: 10, background: 'var(--red)', color: '#fff',
                        borderRadius: 10, padding: '1px 6px', fontWeight: 700, flexShrink: 0 }}>{overdue.length}</span>}
                  </div>
                  <div style={{ fontSize: 11.5, opacity: active ? 0.85 : 0.65 }}>
                    {day < 1
                      ? `Starts ${fmtDate(client.start_date || client.created_at)}`
                      : `Day ${day}`}
                    {' · '}{next ? `${getTouchpoint(next.kind)?.short || 'Next'} ${fmtDate(next.due_date)}` : 'All done'}
                    {st === 'overdue' && !active && <span style={{ color: 'var(--red)' }}> · overdue</span>}
                  </div>
                  {/* Flags at a glance, so the queue is scannable without clicking in */}
                  {flagList.length > 0 && (
                    <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', marginTop: 4 }}>
                      {flagList.slice(0, 3).map(f => {
                        const g = FLAG_GROUPS[f.group]
                        return (
                          <span key={f.key} title={f.then}
                            style={{ fontSize: 9.5, padding: '1px 5px', borderRadius: 4,
                              background: active ? 'rgba(255,255,255,0.2)' : g.bg,
                              color: active ? '#fff' : g.color, fontWeight: 600 }}>
                            {f.label}
                          </span>
                        )
                      })}
                      {flagList.length > 3 && (
                        <span style={{ fontSize: 9.5, opacity: 0.7 }}>+{flagList.length - 3}</span>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
        )}

        {/* Detail */}
        <div style={{ flex: 1, minWidth: queueOpen ? 460 : 320 }}>
          {!selected ? (
            <div className="empty-state">
              <Target size={40} className="empty-state-icon" />
              <h3>Nothing in the first 90 days right now</h3>
              <p>New clients appear here automatically when they're added.</p>
            </div>
          ) : (
            <>
              {/* Header — who, where they are, and what's next, in one line */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10,
                flexWrap: 'wrap' }}>
                <span style={{ fontFamily: 'var(--font-display)', fontSize: 24, color: 'var(--navy)' }}>
                  {selected.client.name}
                </span>
                {goal?.health_status && (
                  <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 50,
                    background: HEALTH[goal.health_status].bg, color: HEALTH[goal.health_status].color }}>
                    {HEALTH[goal.health_status].label}
                  </span>
                )}
                {PHASES[phaseTab].next && (
                  <button className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }}
                    title={`Move to ${PHASES[PHASES[phaseTab].next].label}`}
                    onClick={() => setGradModal(selected)}>
                    <GraduationCap size={13} /> Move to {PHASES[PHASES[phaseTab].next].label}
                  </button>
                )}
              </div>

              {/* Day counter with the next thing due */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14,
                padding: '11px 14px', borderRadius: 10, background: 'var(--cream)',
                flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, color: 'var(--navy)',
                    lineHeight: 1 }}>
                    {selected.day < 1 ? `Starts in ${1 - selected.day}d` : `Day ${selected.day}`}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                    started {fmtDate(selected.client.start_date || selected.client.created_at)}
                  </div>
                </div>
                <div style={{ flex: 1, minWidth: 150 }}>
                  <div style={{ height: 6, background: '#fff', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ width: `${Math.min(100, (selected.day / 90) * 100)}%`,
                      height: '100%', background: 'var(--gold)' }} />
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase',
                    letterSpacing: 0.4 }}>Next up</div>
                  <div style={{ fontSize: 13, fontWeight: 600,
                    color: selected.overdue.length ? 'var(--red)' : 'var(--text-primary)' }}>
                    {selected.next
                      ? `${getTouchpoint(selected.next.kind)?.label.split('—')[0].trim()} · ${fmtDate(selected.next.due_date)}`
                      : 'Nothing outstanding'}
                  </div>
                </div>
              </div>

              {/* Anything raised in the AD check-in, before anything else */}
              <WatchBanner entry={watch.find(w => w.client_id === selectedId)} />

              {/* Schedule at a glance — click one to open its detail */}
              <TouchpointStrip
                tps={selected.tps}
                defs={phaseTab === 'establishing' ? ESTABLISHING_TOUCHPOINTS : TOUCHPOINTS}
                activeKind={expanded}
                onOpen={setExpanded} />

              {selected.tps.length === 0 && (
                <div className="card" style={{ marginBottom: 14 }}>
                  <div className="card-body" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 12.5, color: 'var(--text-muted)', flex: 1 }}>
                      This client predates the 90-day flow — no touchpoints yet.
                    </span>
                    <button className="btn btn-ghost btn-sm" disabled={generating}
                      onClick={() => generateFor(selected.client.id)}>
                      <RefreshCw size={12} /> {generating ? 'Creating…' : 'Create touchpoints'}
                    </button>
                  </div>
                </div>
              )}

              {/* The open touchpoint, if any */}
              {expanded && (() => {
                const defs = phaseTab === 'establishing' ? ESTABLISHING_TOUCHPOINTS : TOUCHPOINTS
                const def = defs.find(d => d.kind === expanded)
                const tp = selected.tps.find(t => t.kind === expanded)
                if (!def || !tp) return null
                return (
                  <div style={{ marginBottom: 14 }}>
                    <TouchpointCard tp={tp} def={def} client={selected.client} goal={goal}
                      onComplete={completeTouchpoint} onSkip={skipTouchpoint}
                      expanded onToggle={() => setExpanded(null)} />
                  </div>
                )
              })()}

              {/* The main event: what we're aiming for and what we're delivering */}
              <GoalsPanel
                goals={clientGoals.filter(g => g.client_id === selectedId)}
                checkins={checkins.filter(c => c.client_id === selectedId)}
                onAdd={addGoal} onUpdate={updateGoal} onCheckin={addCheckin} />

              <CommitmentsPanel
                rows={commitments.filter(c => c.client_id === selectedId)}
                onAdd={addCommitment} onUpdate={updateCommitment}
                notes={cNotes.filter(n => n.client_id === selectedId)}
                onAddNote={addCommitmentNote} />

              <ClientNotesPanel clientId={selectedId} currentUser={currentUser} profiles={profiles} />

              {/* Context, below the fold */}
              <StatusPanel goal={goal} onSave={saveRelationship} />

              <KickoffMeetingNotes decks={decks.filter(d => d.client_id === selectedId)} />

              <KickoffNotes row={goal} onSave={saveGoal} />

              <DeliveryPanel client={selected.client} tasks={tasks} onOpenTasks={onOpenTasks} />
            </>
          )}
        </div>
      </div>

      {gradModal && (
        <GraduateModal
          client={gradModal.client}
          phase={phaseTab}
          openCount={gradModal.open.length}
          onClose={() => setGradModal(null)}
          onConfirm={(wins, note) => graduate(gradModal.client.id, wins, note)}
        />
      )}
    </div>
  )
}